import {
  action, computed, makeObservable, observable,
} from 'mobx';
import { nanoid } from 'nanoid';
import type { ConsentStore } from '@src/state/ConsentStore.js';
import { FeatureValue } from '@src/types/comsApi.js';
import { ModifiedState, TefComs } from '@src/types/typings.js';

export class FeatureValueStore implements FeatureValue {
  private $fid: string;

  description: string;
  granted?: boolean = undefined;
  originalGranted?: boolean = undefined;
  id: string;
  title: string;

  belongsToConsent: ConsentStore;

  constructor({
    id, description, title, granted, ...restData
  }: FeatureValue, consent: ConsentStore) {
    this.belongsToConsent = consent;
    makeObservable(this, {
      granted: observable,
      setGranted: action,
      resetToInitial: action,
      isRevokedOrGranted: computed,
      modified: computed,
    });
    this.id = id;
    this.description = description;
    this.title = title;
    this.granted = granted;
    this.originalGranted = granted;
    Object.assign(this, restData);
    this.$fid = nanoid();
  }

  // Just to be compatible with ConsentStore Interface for using in TefComsTriStateButton
  change(value: boolean): void {
    this.setGranted(value);
  }

  setGranted(granted: boolean): void {
    const grantedOld = this.granted;
    this.granted = granted;
    if (grantedOld !== granted) {
      this.belongsToConsent.setGrantedAccordingToFeatures();
    }
  }

  get fid(): string {
    return this.$fid;
  }

  // Determines if the feature has been either explicitly granted or revoked.
  get isRevokedOrGranted() {
    return typeof this.granted !== 'undefined';
  }

  /**
   * Retrieves the modified state based on the current and initial granted values.
   * If the current granted value matches the initial granted value, the method returns undefined.
   * Otherwise, returns the state as 'granted' or 'revoked' depending on the current granted value.
   *
   * @return {ModifiedState | undefined} The modified state, 'granted', 'revoked', or undefined if no change occurred.
   */
  get modified(): ModifiedState | undefined {
    if (this.granted === this.originalGranted) {
      return undefined;
    }

    return this.granted ? 'granted' : 'revoked';
  }

  toJSON(): FeatureValue {
    const { belongsToConsent, ...rest } = { ...this };
    return rest;
  }

  toSaveFeatureValue(): TefComs.ShortFeatureValue {
    const { id, granted = false } = this;
    return { id, granted };
  }

  toShortFeatureValue(): TefComs.ShortFeatureValue {
    return this.toSaveFeatureValue();
  }

  /**
   * Resets the granted property to its initial state.
   *
   * @return {void} Does not return a value.
   */
  resetToInitial() {
    this.granted = this.originalGranted;
  }
}
